import { api } from './api';

// Payroll Runs
export const getPayrollRuns = async (params = {}) => {
  try {
    const response = await api.get('/payroll/runs', { params });
    return response;
  } catch (error) {
    // Return empty data structure if API endpoint doesn't exist yet
    if (error.response?.status === 404) {
      return { data: { payrollRuns: [] }, meta: null, status: 'success' };
    }
    throw error;
  }
};

export const getPayrollRun = async (id) => {
  const response = await api.get(`/payroll/runs/${id}`);
  return response;
};

export const createPayrollRun = async (data) => {
  const response = await api.post('/payroll/runs', data);
  return response;
};

export const updatePayrollRun = async (id, data) => {
  const response = await api.put(`/payroll/runs/${id}`, data);
  return response;
};

export const deletePayrollRun = async (id) => {
  const response = await api.delete(`/payroll/runs/${id}`);
  return response;
};

export const processPayroll = async (id) => {
  const response = await api.post(`/payroll/runs/${id}/process`);
  return response;
};

export const approvePayroll = async (id) => {
  const response = await api.post(`/payroll/runs/${id}/approve`);
  return response;
};

export const finalizePayroll = async (id) => {
  const response = await api.post(`/payroll/runs/${id}/finalize`);
  return response;
};

// Payslips
export const getPayslips = async (params = {}) => {
  try {
    const response = await api.get('/payroll/payslips', { params });
    return response;
  } catch (error) {
    // Return empty data structure if API endpoint doesn't exist yet
    if (error.response?.status === 404) {
      return { data: { payslips: [] }, meta: null, status: 'success' };
    }
    throw error;
  }
};

export const getPayslip = async (id) => {
  const response = await api.get(`/payroll/payslips/${id}`);
  return response;
};

export const downloadPayslip = async (id) => {
  const response = await api.get(`/payroll/payslips/${id}/download`, {
    responseType: 'blob'
  });
  
  // Create a download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `payslip-${id}.pdf`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return response;
};

// Employee Payslips
export const getEmployeePayslips = async (employeeId, params = {}) => {
  const response = await api.get(`/payroll/employees/${employeeId}/payslips`, { params });
  return response;
};

// Payroll Summary & Reports
export const getPayrollSummary = async (params = {}) => {
  try {
    const response = await api.get('/payroll/summary', { params });
    return response;
  } catch (error) {
    // Return empty data structure if API endpoint doesn't exist yet
    if (error.response?.status === 404) {
      return { 
        data: {
          summary: {
            totalEmployees: 0,
            totalGrossPay: 0,
            totalDeductions: 0,
            totalNetPay: 0,
            averageSalary: 0,
            departmentBreakdown: [],
            trends: []
          }
        }, 
        status: 'success' 
      };
    }
    throw error;
  }
};

export const getPayrollReport = async (params = {}) => {
  const response = await api.get('/payroll/reports', { params });
  return response;
};

export const exportPayrollReport = async (format = 'pdf', params = {}) => {
  const response = await api.get(`/payroll/reports/export`, {
    params: { format, ...params },
    responseType: 'blob'
  });
  
  // Create a download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `payroll-report.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return response;
};

// Payroll Settings
export const getPayrollSettings = async () => {
  const response = await api.get('/payroll/settings');
  return response;
};

export const updatePayrollSettings = async (data) => {
  const response = await api.put('/payroll/settings', data);
  return response;
};

// Pay Components
export const getPayComponents = async () => {
  const response = await api.get('/payroll/settings/components');
  return response;
};

export const createPayComponent = async (data) => {
  const response = await api.post('/payroll/settings/components', data);
  return response;
};

export const updatePayComponent = async (id, data) => {
  const response = await api.put(`/payroll/settings/components/${id}`, data);
  return response;
};

export const deletePayComponent = async (id) => {
  const response = await api.delete(`/payroll/settings/components/${id}`);
  return response;
};

// Deduction Rules
export const getDeductionRules = async () => {
  const response = await api.get('/payroll/settings/deductions');
  return response;
};

export const createDeductionRule = async (data) => {
  const response = await api.post('/payroll/settings/deductions', data);
  return response;
};

export const updateDeductionRule = async (id, data) => {
  const response = await api.put(`/payroll/settings/deductions/${id}`, data);
  return response;
};

export const deleteDeductionRule = async (id) => {
  const response = await api.delete(`/payroll/settings/deductions/${id}`);
  return response;
};

// Payroll Adjustments
export const addPayrollAdjustment = async (payrollRunId, employeeId, adjustment) => {
  const response = await api.post(`/payroll/runs/${payrollRunId}/employees/${employeeId}/adjustments`, adjustment);
  return response;
};

export const removePayrollAdjustment = async (payrollRunId, employeeId, adjustmentId) => {
  const response = await api.delete(`/payroll/runs/${payrollRunId}/employees/${employeeId}/adjustments/${adjustmentId}`);
  return response;
};

// Payroll Dashboard
export const getPayrollDashboard = async (params = {}) => {
  const response = await api.get('/payroll/dashboard', { params });
  return response;
};

// Bank Export
export const exportBankFile = async (payrollRunId, format = 'ach') => {
  const response = await api.get(`/payroll/runs/${payrollRunId}/export/bank`, {
    params: { format },
    responseType: 'blob'
  });
  
  // Create a download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', `bank-export-${payrollRunId}.${format}`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return response;
};